/**
 * Public Portfolio Page
 *
 * View another user's public portfolio. Supports viewing by slug or userId.
 * Includes authority summary, highlights, and hire request functionality.
 */

import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
  useGetPortfolioBySlugQuery,
  useGetPublicPortfolioQuery,
  useGetPublicProfileBySlugQuery,
  useLazyListenToClipQuery,
  PublicProfileResponse,
  DifficultyTier,
} from '../store/api/portfolioApi';
import {
  useCreateHireRequestMutation,
  useRecordListenMutation,
} from '../store/api/marketplaceApi';
import { useCreateReportMutation, type ReportTargetType } from '../store/api/reportsApi';
import {
  useSaveCandidateMutation,
  useUnsaveCandidateMutation,
  useCheckCandidateSavedQuery,
} from '../store/api/recruiterApi';
import {
  useGetUserCredentialsQuery,
  type PublicDomainCredential,
} from '../store/api/credentialsApi';
import {
  useGetPublicTimelineBySlugQuery,
  TIMELINE_ICONS,
  TIMELINE_EVENT_COLORS,
} from '../store/api/timelineApi';
import { useAuth } from '../hooks/useAuth';
import { useAnalytics } from '../hooks/useAnalytics';
import { SEO, getPublicProfileOgImageUrl } from '../components/seo';
import { PublicEndorsements } from '../components/endorsements/PublicEndorsements';
import { ResultProvenance } from '../components/portfolio/ResultProvenance';

/**
 * Content reporting has no store yet: POST /reports answers 501
 * NOT_AVAILABLE. Until reports are persisted (and someone can review them)
 * no Report button is offered — it used to say "Report submitted. Our team
 * will review your report" about a report that was thrown away.
 */
export const CONTENT_REPORTING_AVAILABLE = false;

export function PublicPortfolioPage() {
  const { slug, userId } = useParams<{ slug?: string; userId?: string }>();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [showHireModal, setShowHireModal] = useState(false);
  const [hireMessage, setHireMessage] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');

  // Every slug route renders the PUBLISHED public profile (GET /public-profiles/:slug,
  // visibility settings applied server-side, published items only).
  // /p/:slug names the same profile as /u/:slug (the same portfolio slug, the same
  // isPublic filter), so it renders the same view. It used to read the legacy
  // portfolio-by-slug response through a branch that called hooks after its early
  // returns: once the 200 arrived React threw on the changed hook count and
  // /view/p/:slug rendered nothing at all.
  const isNewPublicProfileRoute =
    location.pathname.startsWith('/u/') ||
    location.pathname.startsWith('/view/u/') ||
    location.pathname.startsWith('/p/') ||
    location.pathname.startsWith('/view/p/');

  // Fetch using new public profile endpoint for /u/:slug routes
  const {
    data: publicProfileData,
    isLoading: publicProfileLoading,
    error: publicProfileError,
  } = useGetPublicProfileBySlugQuery(slug!, { skip: !slug || !isNewPublicProfileRoute });

  // Fetch by slug (old /p/:slug route)
  const {
    data: slugData,
    isLoading: slugLoading,
    error: slugError,
  } = useGetPortfolioBySlugQuery(slug!, { skip: !slug || isNewPublicProfileRoute });

  // Fetch by userId
  const {
    data: userIdData,
    isLoading: userIdLoading,
    error: userIdError,
  } = useGetPublicPortfolioQuery(userId!, { skip: !userId });

  const [createHireRequest, { isLoading: isCreatingRequest }] = useCreateHireRequestMutation();
  const [recordListen] = useRecordListenMutation();
  const [createReport, { isLoading: isCreatingReport }] = useCreateReportMutation();
  const [listenToClip] = useLazyListenToClipQuery();
  const { track } = useAnalytics();

  // Get the user ID for credentials query (only for legacy routes, new route has certs included)
  const targetUserId = isNewPublicProfileRoute
    ? undefined // New route includes certifications in response
    : slug
      ? slugData?.portfolio?.userId
      : userId;

  // Fetch passed domain credentials
  const { data: credentials } = useGetUserCredentialsQuery(targetUserId!, {
    skip: !targetUserId,
  });

  // Track profile view when data loads successfully
  useEffect(() => {
    if (slug && (slugData || publicProfileData)) {
      track('profile_shared', { profileSlug: slug });
    } else if (userId && userIdData) {
      track('profile_shared', { profileSlug: userId });
    }
  }, [slug, userId, slugData, publicProfileData, userIdData, track]);

  // Determine which data source to use
  const isLoading = isNewPublicProfileRoute
    ? publicProfileLoading
    : slug
      ? slugLoading
      : userIdLoading;
  const error = isNewPublicProfileRoute
    ? publicProfileError
    : slug
      ? slugError
      : userIdError;

  // For new public profile routes, use the new data format
  // For old routes, use the existing portfolio format
  const data = isNewPublicProfileRoute ? null : (slug ? slugData : userIdData);
  const newProfileData: PublicProfileResponse | undefined = isNewPublicProfileRoute ? publicProfileData : undefined;

  // Hooks run on EVERY render, before any early return (rules of hooks).
  // Save candidate (recruiter feature)
  const [saveCand] = useSaveCandidateMutation();
  const [unsaveCand] = useUnsaveCandidateMutation();
  const candidateUserId = data?.portfolio?.userId;
  const { data: savedCheck } = useCheckCandidateSavedQuery(candidateUserId || '', {
    skip: !candidateUserId || !isAuthenticated,
  });
  const [isCandidateSaved, setIsCandidateSaved] = useState(false);
  if (savedCheck?.isSaved && !isCandidateSaved) setIsCandidateSaved(true);

  const handleToggleSaveCandidate = async () => {
    if (!candidateUserId) return;
    try {
      if (isCandidateSaved) {
        await unsaveCand(candidateUserId).unwrap();
        setIsCandidateSaved(false);
      } else {
        await saveCand({ candidateUserId }).unwrap();
        setIsCandidateSaved(true);
      }
    } catch {
      // Silently fail — recruiter feature may not be available
    }
  };

  const handleHireRequest = async () => {
    if (!data?.portfolio || !hireMessage.trim()) return;

    try {
      await createHireRequest({
        targetUserId: data.portfolio.userId,
        portfolioId: data.portfolio.id,
        message: hireMessage,
        context: 'portfolio',
      }).unwrap();
      setShowHireModal(false);
      setHireMessage('');
      alert('Hire request sent successfully!');
    } catch (err) {
      console.error('Failed to send hire request:', err);
      alert('Failed to send hire request');
    }
  };

  const handlePlayClip = async (highlightId: string, isPreview: boolean) => {
    if (!data?.portfolio) return;

    try {
      await recordListen({
        targetUserId: data.portfolio.userId,
        highlightId,
        portfolioId: data.portfolio.id,
        listenType: isPreview ? 'preview' : 'full',
      }).unwrap();
    } catch (err) {
      console.error('Failed to record listen:', err);
    }
  };

  const handleReport = async (targetType: ReportTargetType, targetId: string) => {
    if (!reportReason.trim()) return;

    try {
      await createReport({
        targetType,
        targetId,
        reason: reportReason,
      }).unwrap();
      setShowReportModal(false);
      setReportReason('');
      alert('Report submitted successfully. Thank you for helping keep our community safe.');
    } catch (err) {
      console.error('Failed to submit report:', err);
      alert('Failed to submit report. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Check if we have valid data
  const hasData = isNewPublicProfileRoute ? !!newProfileData : !!data;

  if (error || !hasData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {/* SEO: noindex for non-public/missing profiles */}
        <SEO
          title="Profile Not Found"
          description="This profile doesn't exist or is not public."
          noIndex={true}
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h1>
          <p className="text-gray-500 mb-4">
            This profile doesn't exist or is not public.
          </p>
          <Link to="/marketplace" className="btn btn-primary">
            Browse Marketplace
          </Link>
        </div>
      </div>
    );
  }

  // Handle hire request for new public profile
  const handleNewProfileHireRequest = async (message: string) => {
    if (!newProfileData?.hire?.portfolioId || !newProfileData?.hire?.userId) return;

    await createHireRequest({
      targetUserId: newProfileData.hire.userId,
      portfolioId: newProfileData.hire.portfolioId,
      message,
      context: 'public_profile',
    }).unwrap();
  };

  // Handle new public profile route
  if (isNewPublicProfileRoute && newProfileData) {
    return (
      <NewPublicProfileView
        profile={newProfileData}
        slug={slug!}
        credentials={credentials || []}
        isAuthenticated={isAuthenticated}
        showReportModal={showReportModal}
        setShowReportModal={setShowReportModal}
        reportReason={reportReason}
        setReportReason={setReportReason}
        handleReport={handleReport}
        isCreatingReport={isCreatingReport}
        listenToClip={listenToClip}
        onHireRequest={handleNewProfileHireRequest}
        isCreatingHireRequest={isCreatingRequest}
      />
    );
  }


  // Handle old portfolio routes
  const { portfolio, highlights, authoritySummary, verifiedRoles, endorsements } = data!;

  // Build SEO description for legacy portfolio
  const legacySeoDescription = portfolio.headline
    || (authoritySummary?.overallAuthorityScore
      ? `Professional with ${authoritySummary.overallAuthorityScore.toFixed(0)}% performance score on BeHeard`
      : 'Professional on BeHeard — practising real conversations');

  return (
    <div className={`min-h-screen theme-${portfolio.theme || 'default'}`}>
      {/* SEO Meta Tags */}
      <SEO
        title={portfolio.displayName || 'Profile'}
        description={legacySeoDescription}
        canonicalPath={slug ? `/p/${slug}` : undefined}
        ogType="profile"
        ogImage={slug ? getPublicProfileOgImageUrl(slug) : undefined}
        twitterCard="summary_large_image"
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold">
              {(portfolio.displayName || 'U')[0].toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold">
                {portfolio.displayName || 'Anonymous'}
              </h1>
              {portfolio.headline && (
                <p className="text-primary-100 mt-1 text-lg">{portfolio.headline}</p>
              )}
              <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-3 text-primary-100 text-sm">
                {portfolio.location && (
                  <span className="flex items-center gap-1">
                    <LocationIcon className="w-4 h-4" />
                    {portfolio.location}
                  </span>
                )}
                {portfolio.rateType !== 'none' && portfolio.rateAmount && (
                  <span className="flex items-center gap-1">
                    <CurrencyIcon className="w-4 h-4" />
                    ${portfolio.rateAmount}/{portfolio.rateType}
                  </span>
                )}
              </div>

              {/* Key signals — limited to 3 most meaningful for recruiters */}
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-4">
                {authoritySummary?.overallAuthorityScore != null && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium">
                    {authoritySummary.overallAuthorityScore.toFixed(0)} Performance Score
                  </span>
                )}
                {authoritySummary?.totalAssessments != null && authoritySummary.totalAssessments > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 rounded-full text-sm">
                    {authoritySummary.totalAssessments} completed session{authoritySummary.totalAssessments !== 1 ? 's' : ''}
                  </span>
                )}
                {credentials && credentials.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-400/20 rounded-full text-sm">
                    <CheckIcon className="w-3.5 h-3.5" />
                    {credentials.length} certified
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col items-center gap-2">
              {portfolio.availableForHire && (
                <>
                  <button
                    onClick={() => setShowHireModal(true)}
                    className="btn bg-white text-primary-700 hover:bg-primary-50 font-semibold px-6 py-3 shadow-lg"
                  >
                    Contact for Hire
                  </button>
                  <span className="text-primary-200 text-xs">Listen to sample calls first</span>
                </>
              )}
              {isAuthenticated && (
                <div className="flex gap-2">
                  <button
                    onClick={handleToggleSaveCandidate}
                    className={`btn border border-white/30 ${
                      isCandidateSaved
                        ? 'bg-red-500/20 text-white hover:bg-red-500/30'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    title={isCandidateSaved ? 'Remove from shortlist' : 'Save to shortlist'}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={isCandidateSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  {CONTENT_REPORTING_AVAILABLE && (
                    <button
                      onClick={() => setShowReportModal(true)}
                      className="btn bg-white/10 text-white hover:bg-white/20 border border-white/30"
                      title="Report this profile"
                    >
                      <FlagIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        {/* SECTION 1: Featured Sample Calls — primary proof, shown first */}
        {highlights && highlights.filter(h => h.isPublic && h.orderIndex >= 1 && h.orderIndex <= 3).length > 0 && (
          <div className="card border-primary-200">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Featured Sample Calls</h2>
              <p className="text-xs text-gray-500 mt-0.5">Hear how they perform in real conversations</p>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 gap-4">
                {highlights
                  .filter(h => h.isPublic && h.orderIndex >= 1 && h.orderIndex <= 3)
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((highlight, idx) => (
                  <div
                    key={highlight.id}
                    className={`p-4 rounded-lg transition-colors ${
                      idx === 0
                        ? 'border-2 border-primary-300 bg-primary-50/50'
                        : 'border border-primary-200 bg-primary-50/20'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <button
                        onClick={() => handlePlayClip(highlight.id, true)}
                        className={`flex-shrink-0 rounded-full flex items-center justify-center transition-colors shadow-sm ${
                          idx === 0
                            ? 'w-14 h-14 bg-primary-600 hover:bg-primary-700'
                            : 'w-12 h-12 bg-primary-100 hover:bg-primary-200'
                        }`}
                      >
                        <PlayIcon className={`${idx === 0 ? 'w-7 h-7 text-white' : 'w-6 h-6 text-primary-600'}`} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{highlight.title}</h4>
                          {idx === 0 && (
                            <span className="px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded font-medium">Best</span>
                          )}
                        </div>
                        {highlight.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{highlight.description}</p>
                        )}
                        {highlight.result && <ResultProvenance result={highlight.result} testId="public-result-provenance" />}
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          {highlight.durationSeconds && (
                            <span>
                              {Math.floor(highlight.durationSeconds / 60)}:{String(highlight.durationSeconds % 60).padStart(2, '0')}
                            </span>
                          )}
                          {highlight.scenarioKey && (
                            <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded">
                              {highlight.scenarioKey.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SECTION 2: Skills */}
        {authoritySummary && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Skills</h2>
            </div>
            <div className="card-body">
              {/* Skill areas — the primary proof section */}
              {authoritySummary.topDomains && authoritySummary.topDomains.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Skill areas</h3>
                  <div className="space-y-2">
                    {authoritySummary.topDomains.slice(0, 3).map((domain) => (
                      <div key={domain.domainId} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium">{domain.domainName}</span>
                            <span className="text-gray-500">
                              {domain.objectivesPassed}/{domain.totalObjectives} passed
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-500 rounded-full"
                              style={{ width: `${domain.authorityScore}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-sm font-medium text-primary-600">
                          {domain.authorityScore.toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Achievements */}
              {authoritySummary.badges && authoritySummary.badges.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Achievements</h3>
                  <div className="flex flex-wrap gap-2">
                    {authoritySummary.badges.map((badge) => (
                      <span
                        key={badge.id}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
                      >
                        <StarIcon className="w-4 h-4" />
                        {badge.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bio */}
        {portfolio.bio && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">About</h2>
            </div>
            <div className="card-body">
              <p className="text-gray-600 whitespace-pre-wrap">{portfolio.bio}</p>
            </div>
          </div>
        )}

        {/* Verified Roles */}
        {verifiedRoles && verifiedRoles.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Verified Roles</h2>
            </div>
            <div className="card-body">
              <div className="flex flex-wrap gap-2">
                {verifiedRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    <CheckIcon className="w-4 h-4" />
                    {role}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Manager endorsements the learner published (P4.1) */}
        <PublicEndorsements endorsements={endorsements} />

        {/* Domain Credentials */}
        {credentials && credentials.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <CertificateIcon className="w-5 h-5 text-green-600" />
                <h2 className="font-semibold text-gray-900">Verified Certifications</h2>
              </div>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {credentials.map((cred) => (
                  <div
                    key={cred.domainKey}
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckIcon className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{cred.label}</div>
                        <div className="text-xs text-gray-500 capitalize">
                          {cred.category.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{cred.overallScore}%</div>
                      <div className="text-xs text-gray-400">Score</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* More Sample Calls — remaining public, non-featured */}
        {highlights && highlights.filter(h => h.isPublic && (h.orderIndex === 0 || h.orderIndex > 3)).length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">More Sample Calls</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {highlights
                  .filter(h => h.isPublic && (h.orderIndex === 0 || h.orderIndex > 3))
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((highlight) => (
                  <div
                    key={highlight.id}
                    className="p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handlePlayClip(highlight.id, true)}
                        className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                      >
                        <PlayIcon className="w-5 h-5 text-gray-600" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{highlight.title}</h4>
                        {highlight.result && <ResultProvenance result={highlight.result} testId="public-result-provenance" />}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          {highlight.durationSeconds && (
                            <span>
                              {Math.floor(highlight.durationSeconds / 60)}:{String(highlight.durationSeconds % 60).padStart(2, '0')}
                            </span>
                          )}
                          {highlight.scenarioKey && (
                            <span className="px-2 py-0.5 bg-gray-100 rounded">
                              {highlight.scenarioKey.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Custom Sections */}
        {portfolio.customSections && portfolio.customSections.length > 0 && (
          <>
            {portfolio.customSections
              .filter(section => section.isVisible)
              .sort((a, b) => a.order - b.order)
              .map((section) => (
                <div key={section.id} className="card">
                  <div className="card-header">
                    <h2 className="font-semibold text-gray-900">{section.title}</h2>
                  </div>
                  <div className="card-body">
                    <p className="text-gray-600 whitespace-pre-wrap">{section.content}</p>
                  </div>
                </div>
              ))}
          </>
        )}
      </div>

      {/* Hire Modal */}
      {showHireModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Contact {portfolio.displayName || 'User'}
              </h3>
              <textarea
                value={hireMessage}
                onChange={(e) => setHireMessage(e.target.value)}
                placeholder="Write your message..."
                className="input w-full h-32 mb-4"
                maxLength={5000}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowHireModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleHireRequest}
                  disabled={isCreatingRequest || !hireMessage.trim()}
                  className="btn btn-primary"
                >
                  {isCreatingRequest ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Report Profile
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Help us understand why you're reporting this profile. Our team will review your report.
              </p>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Describe the issue..."
                className="input w-full h-32 mb-4"
                maxLength={2000}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('');
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReport('profile', portfolio.userId)}
                  disabled={isCreatingReport || !reportReason.trim()}
                  className="btn bg-red-600 text-white hover:bg-red-700"
                >
                  {isCreatingReport ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Icon components
function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function CurrencyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * New Public Profile View Component
 * Renders the new public profile format with authority scores, badges, certifications, NPC progress, and featured clips
 */
interface NewPublicProfileViewProps {
  profile: PublicProfileResponse;
  slug: string;
  credentials: PublicDomainCredential[];
  isAuthenticated: boolean;
  showReportModal: boolean;
  setShowReportModal: (show: boolean) => void;
  reportReason: string;
  setReportReason: (reason: string) => void;
  handleReport: (targetType: ReportTargetType, targetId: string) => Promise<void>;
  isCreatingReport: boolean;
  listenToClip: (args: { slug: string; clipId: string }) => void;
  // Hire functionality
  onHireRequest: (message: string) => Promise<void>;
  isCreatingHireRequest: boolean;
}

// Tier color helpers
const TIER_COLORS: Record<DifficultyTier, { bg: string; text: string; border: string }> = {
  bronze: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  silver: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  platinum: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
  master: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
};

function NewPublicProfileView({
  profile,
  slug,
  credentials,
  isAuthenticated,
  showReportModal,
  setShowReportModal,
  reportReason,
  setReportReason,
  handleReport,
  isCreatingReport,
  listenToClip,
  onHireRequest,
  isCreatingHireRequest,
}: NewPublicProfileViewProps) {
  const [playingClipId, setPlayingClipId] = useState<string | null>(null);
  const [showHireModal, setShowHireModal] = useState(false);
  const [hireMessage, setHireMessage] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if profile owner is available for hire
  const isAvailableForHire = profile.hire?.availableForHire ?? false;

  const handleHireClick = () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(window.location.pathname);
      window.location.href = `/login?returnTo=${returnUrl}`;
      return;
    }
    setShowHireModal(true);
  };

  const handleSubmitHire = async () => {
    if (!hireMessage.trim()) return;
    try {
      await onHireRequest(hireMessage);
      setShowHireModal(false);
      setHireMessage('');
      alert('Hire request sent successfully!');
    } catch (err) {
      console.error('Failed to send hire request:', err);
      alert('Failed to send hire request');
    }
  };

  // Fetch public timeline for Growth Timeline section
  const { data: timelineData, isLoading: timelineLoading } = useGetPublicTimelineBySlugQuery({
    slug,
    limit: 10,
  });

  const handlePlayClip = async (clipId: string) => {
    setPlayingClipId(clipId);
    try {
      await listenToClip({ slug, clipId });
    } catch (err) {
      console.error('Failed to load clip:', err);
    }
  };

  // Build SEO description from profile data
  const seoDescription = profile.user.headline
    || (profile.authority ? `${profile.authority.tier} tier communicator on BeHeard` : 'Communication professional on BeHeard');

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title={profile.user.displayName || 'Profile'}
        description={seoDescription}
        canonicalPath={`/u/${slug}`}
        ogType="profile"
        ogImage={getPublicProfileOgImageUrl(slug)}
        twitterCard="summary_large_image"
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Avatar */}
            {profile.user.avatarUrl ? (
              <img
                src={profile.user.avatarUrl}
                alt={profile.user.displayName}
                className="w-24 h-24 rounded-full object-cover border-4 border-white/30"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold">
                {(profile.user.displayName || 'U')[0].toUpperCase()}
              </div>
            )}

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold">
                {profile.user.displayName || 'Anonymous'}
              </h1>
              {profile.user.headline && (
                <p className="text-primary-100 mt-1 text-lg">{profile.user.headline}</p>
              )}
              <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-3 text-primary-100 text-sm">
                {profile.user.location && (
                  <span className="flex items-center gap-1">
                    <LocationIcon className="w-4 h-4" />
                    {profile.user.location}
                  </span>
                )}
                {profile.authority && (
                  <span className="flex items-center gap-1">
                    <StarIcon className="w-4 h-4" />
                    {profile.authority.tier} tier
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {isAvailableForHire && (
                <button
                  onClick={handleHireClick}
                  className="btn bg-white text-primary-700 hover:bg-primary-50"
                >
                  Contact for Hire
                </button>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="btn bg-white/10 text-white hover:bg-white/20 border border-white/30"
                title="Share this profile"
              >
                <ShareIcon className="w-5 h-5" />
              </button>
              {isAuthenticated && CONTENT_REPORTING_AVAILABLE && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="btn bg-white/10 text-white hover:bg-white/20 border border-white/30"
                  title="Report this profile"
                >
                  <FlagIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Authority Summary */}
        {profile.authority && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Authority Summary</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-primary-50 rounded-lg">
                  <div className="text-3xl font-bold text-primary-600">
                    {profile.authority.score}
                  </div>
                  <div className="text-sm text-gray-500">Authority Score</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-900 capitalize">
                    {profile.authority.tier}
                  </div>
                  <div className="text-sm text-gray-500">Tier</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-900">
                    {profile.authority.totalSessions}
                  </div>
                  <div className="text-sm text-gray-500">Sessions</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Badges */}
        {profile.badges && profile.badges.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Badges</h2>
            </div>
            <div className="card-body">
              <div className="flex flex-wrap gap-2">
                {profile.badges.map((badge) => (
                  <span
                    key={badge.key}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm"
                  >
                    <StarIcon className="w-4 h-4" />
                    {badge.key.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Certifications */}
        {profile.certifications && profile.certifications.domains.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <CertificateIcon className="w-5 h-5 text-green-600" />
                <h2 className="font-semibold text-gray-900">Certifications</h2>
              </div>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {profile.certifications.domains.map((cert) => (
                  <div
                    key={cert.domainKey}
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckIcon className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{cert.label}</div>
                        <div className="text-xs text-gray-500 capitalize">
                          {cert.category.replace(/_/g, ' ')} • {cert.level}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{cert.score}%</div>
                      <div className="text-xs text-gray-400">Score</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* NPC Progress */}
        {profile.npcProgress && profile.npcProgress.personas.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <TrophyIcon className="w-5 h-5 text-purple-600" />
                <h2 className="font-semibold text-gray-900">NPC Challenges Progress</h2>
              </div>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {profile.npcProgress.personas.map((persona) => {
                  const tierColors = TIER_COLORS[persona.tier] || TIER_COLORS.bronze;
                  return (
                    <div
                      key={persona.personaKey}
                      className={`flex items-center justify-between p-3 ${tierColors.bg} border ${tierColors.border} rounded-lg`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${tierColors.bg} flex items-center justify-center`}>
                          {persona.isMastered ? (
                            <TrophyIcon className={`w-5 h-5 ${tierColors.text}`} />
                          ) : (
                            <span className={`font-bold ${tierColors.text}`}>
                              {persona.levelsPassed}/{persona.totalLevels}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{persona.personaLabel}</div>
                          <div className={`text-xs ${tierColors.text} capitalize`}>
                            {persona.tier} tier • {persona.isMastered ? 'Mastered' : `${persona.levelsPassed}/${persona.totalLevels} levels`}
                          </div>
                        </div>
                      </div>
                      {persona.isMastered && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${tierColors.bg} ${tierColors.text}`}>
                          Mastered
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Domain Credentials (legacy) */}
        {credentials.length > 0 && !profile.certifications && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <CertificateIcon className="w-5 h-5 text-green-600" />
                <h2 className="font-semibold text-gray-900">Verified Credentials</h2>
              </div>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {credentials.map((cred) => (
                  <div
                    key={cred.domainKey}
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckIcon className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{cred.label}</div>
                        <div className="text-xs text-gray-500 capitalize">
                          {cred.category.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{cred.overallScore}%</div>
                      <div className="text-xs text-gray-400">Score</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Manager endorsements the learner published (P4.1) */}
        <PublicEndorsements endorsements={profile.endorsements} />

        {/* Published portfolio items — each result with the same rubric /
            evidence / overall view share links and the owner's list show. */}
        {profile.highlights && profile.highlights.length > 0 && (
          <div className="card" data-testid="public-highlights">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Highlights</h2>
            </div>
            <div className="card-body">
              <ul className="space-y-3">
                {profile.highlights.map((h) => (
                  <li key={h.id} className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-900">{h.title}</h3>
                    {h.description && <p className="text-sm text-gray-600 mt-1">{h.description}</p>}
                    {h.result && <ResultProvenance result={h.result} testId="public-result-provenance" />}
                    {h.transcriptExcerpt && (
                      <blockquote className="mt-2 text-sm text-gray-700 border-l-4 border-gray-200 pl-3 italic">{h.transcriptExcerpt}</blockquote>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Featured Clips / Samples */}
        {profile.samples && profile.samples.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Featured Clips</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.samples.map((clip) => (
                  <div
                    key={clip.id}
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handlePlayClip(clip.id)}
                        disabled={!clip.canListenPreview}
                        className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                          clip.canListenPreview
                            ? 'bg-primary-100 hover:bg-primary-200 cursor-pointer'
                            : 'bg-gray-100 cursor-not-allowed'
                        }`}
                      >
                        <PlayIcon className={`w-6 h-6 ${clip.canListenPreview ? 'text-primary-600' : 'text-gray-400'}`} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {clip.title}
                        </h4>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          {clip.durationSeconds && (
                            <span>
                              {Math.floor(clip.durationSeconds / 60)}:
                              {String(clip.durationSeconds % 60).padStart(2, '0')}
                            </span>
                          )}
                          <span className="px-2 py-0.5 bg-gray-100 rounded capitalize">
                            {clip.clipType}
                          </span>
                          {playingClipId === clip.id && (
                            <span className="text-primary-600">Loading...</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Growth Timeline */}
        {!timelineLoading && timelineData && timelineData.events.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <TimelineIcon className="w-5 h-5 text-primary-600" />
                <h2 className="font-semibold text-gray-900">Growth Timeline</h2>
              </div>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {timelineData.events.slice(0, 10).map((event) => {
                  const Icon = TIMELINE_ICONS[event.type] || CalendarIcon;
                  const colorName = TIMELINE_EVENT_COLORS[event.type] || 'gray';
                  const bgClass = `bg-${colorName}-100`;
                  const textClass = `text-${colorName}-600`;
                  return (
                    <div key={event.id} className="flex gap-3">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full ${bgClass} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${textClass}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-gray-900">{event.title}</p>
                            {event.description && (
                              <p className="text-sm text-gray-500 mt-0.5">{event.description}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {formatTimelineDate(event.occurredAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hire Modal */}
      {showHireModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Contact {profile.user.displayName || 'User'}
              </h3>
              {profile.hire?.rateType && profile.hire.rateAmount && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                  <span className="text-gray-500">Rate: </span>
                  <span className="font-medium">
                    {profile.hire.currency === 'USD' ? '$' : profile.hire.currency}
                    {profile.hire.rateAmount}/{profile.hire.rateType}
                  </span>
                </div>
              )}
              <textarea
                value={hireMessage}
                onChange={(e) => setHireMessage(e.target.value)}
                placeholder="Introduce yourself and describe what you're looking for..."
                className="input w-full h-32 mb-4"
                maxLength={5000}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowHireModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitHire}
                  disabled={isCreatingHireRequest || !hireMessage.trim()}
                  className="btn btn-primary"
                >
                  {isCreatingHireRequest ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareProfileModal
          slug={slug}
          displayName={profile.user.displayName || 'User'}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Report Profile
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Help us understand why you're reporting this profile. Our team will review your report.
              </p>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Describe the issue..."
                className="input w-full h-32 mb-4"
                maxLength={2000}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('');
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReport('profile', slug)}
                  disabled={isCreatingReport || !reportReason.trim()}
                  className="btn bg-red-600 text-white hover:bg-red-700"
                >
                  {isCreatingReport ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FlagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
      />
    </svg>
  );
}

function CertificateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
      />
    </svg>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function TimelineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );
}

/**
 * Share Profile Modal
 * Allows users to copy the public profile URL or share via social media
 */
interface ShareProfileModalProps {
  slug: string;
  displayName: string;
  onClose: () => void;
}

function ShareProfileModal({ slug, displayName, onClose }: ShareProfileModalProps) {
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}/u/${slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent(`Check out ${displayName}'s profile on BeHeard!`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(`${displayName}'s BeHeard Profile`)}&body=${encodeURIComponent(`Check out this profile: ${publicUrl}`)}`,
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Share Profile
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Copy Link */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile URL
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={publicUrl}
                readOnly
                className="input flex-1 text-sm bg-gray-50"
              />
              <button
                onClick={handleCopy}
                className={`btn ${copied ? 'bg-green-600 text-white' : 'btn-primary'}`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Social Share */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share on
            </label>
            <div className="flex gap-3">
              <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn bg-[#1DA1F2] text-white hover:bg-[#1a8cd8] text-center"
              >
                Twitter
              </a>
              <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn bg-[#0A66C2] text-white hover:bg-[#094d92] text-center"
              >
                LinkedIn
              </a>
              <a
                href={shareLinks.email}
                className="flex-1 btn btn-outline text-center"
              >
                Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Format date for timeline display
function formatTimelineDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}
